import { useTranslation, t } from '../../shared/i18n';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Grid, Popconfirm, Space, Spin, Table, Tag, Tooltip, Typography, message } from 'antd';
import { CloudUploadOutlined, CopyOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dnsService from '../services/dnsService';
import { userPath } from '../../shared/config/paths';

const { Text } = Typography;

const copy = async (text) => {
    try {
        await navigator.clipboard.writeText(String(text));
        message.success(t('Copied'));
    } catch {
        const ta = document.createElement('textarea');
        ta.value = String(text);
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); message.success(t('Copied')); } catch { message.error(t('Failed to copy. Please select the content and copy it manually.')); }
        document.body.removeChild(ta);
    }
};

// long values (DKIM keys) are shortened on screen; copy always takes the full value
export const Copyable = ({ value, strong }) => {
    const [open, setOpen] = useState(false);
    const v = String(value ?? '');
    const long = v.length > 72;
    const shown = long && !open ? `${v.slice(0, 40)}…${v.slice(-16)}` : v;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 2, maxWidth: '100%', flexWrap: 'wrap' }}>
            <Text code={!strong} strong={strong} style={{ wordBreak: 'break-all' }}>{shown}</Text>
            <Button size="small" type="text" icon={<CopyOutlined />} onClick={() => copy(v)} aria-label={t('Copy')} />
            {long && <Button size="small" type="link" style={{ padding: 0 }} onClick={() => setOpen(o => !o)}>{open ? t('Collapse') : t('Expand')}</Button>}
        </span>
    );
};

// zone status (hosted on Yeap DNS) or public-DNS status (not hosted)
const ZONE_TAG = {
    present: { color: 'green', text: 'Already exists' },
    missing: { color: 'orange', text: 'To add' },
    merge: { color: 'blue', text: 'Will merge' },
    conflict: { color: 'red', text: 'Conflict' },
};
const LIVE_TAG = {
    present: { color: 'green', text: 'Active' },
    missing: { color: 'orange', text: 'Not detected' },
    different: { color: 'red', text: 'Mismatch' },
    unknown: { color: 'default', text: 'Query failed' },
};

const recordValue = (r) => (r.type === 'MX' || r.type === 'SRV') && r.priority != null ? `${r.priority} ${r.value}` : r.value;

/**
 * One-click DNS for a product (mail, mail clients, alias domains…).
 * Hosted on Yeap DNS → one button adds / merges what is needed; conflicts change only when ticked.
 * Not hosted → copyable records + a public-DNS check.
 *
 * props: app, id, aid?, title?, onApplied?(result), autoCheck? (public-DNS check on load when not hosted)
 */
const DnsOneClick = ({ app, id, aid, title, onApplied, autoCheck = true }) => {
    const { t } = useTranslation();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const navigate = useNavigate();
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(false);
    const [checking, setChecking] = useState(false);
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState(null);
    const [replace, setReplace] = useState([]);
    // only the latest request may update state (target change / repeated clicks / unmount)
    const seq = useRef(0);
    const alive = useRef(true);
    useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
    const ok = (res) => res && (res.code === 0 || res.success === true);

    const load = useCallback(async (live = false) => {
        if (!app || !id) return;
        const my = ++seq.current;
        const current = () => alive.current && my === seq.current;
        live ? setChecking(true) : setLoading(true);
        setError(null);
        try {
            const res = await dnsService.getOneClickPlan({ app, id, aid, live: live ? 1 : undefined });
            if (!current()) return;
            if (!ok(res)) {
                setError(res?.error || res?.message || t('Failed to load'));
                return;
            }
            setPlan(res.data);
            // not on Yeap DNS: also show what is already live at the customer's provider
            if (!live && autoCheck && res.data && !res.data.hosted) {
                setLoading(false);
                setChecking(true);
                const r2 = await dnsService.getOneClickPlan({ app, id, aid, live: 1 });
                if (current() && ok(r2)) setPlan(r2.data);
            }
        } catch (e) {
            if (current()) setError(e?.message || t('Failed to load'));
        } finally {
            if (current()) { setLoading(false); setChecking(false); }
        }
    }, [app, id, aid, autoCheck, t]);

    useEffect(() => { setReplace([]); setPlan(null); load(false); }, [load]);

    const records = useMemo(() => plan?.records || [], [plan]);
    const hosted = !!plan?.hosted;
    const toAdd = records.filter(r => r.status === 'missing');
    const toMerge = records.filter(r => r.status === 'merge');
    const conflicts = records.filter(r => r.status === 'conflict');
    const toReplace = conflicts.filter(r => replace.includes(r.key) && r.replaceable);
    const actionable = toAdd.length + toMerge.length + toReplace.length;
    // records the plan must still have room for (a replace removes rows as well as adding one)
    const netNew = toAdd.length + toReplace.reduce((n, r) => n + (r.note === 'foreign_mx' ? 0 : 1) - (r.replace_count || 0), 0);
    const quota = plan?.quota;
    const overQuota = hosted && quota && quota.limit >= 0 && netNew > 0 && netNew > (quota.remaining ?? 0);

    const apply = async () => {
        if (applying) return;
        setApplying(true);
        let res;
        try {
            res = await dnsService.applyOneClick({ app, id, aid, replace: toReplace.map(r => r.key) });
        } catch (e) {
            res = { success: false, error: e?.message };
        }
        if (!alive.current) return;
        setApplying(false);
        if (!ok(res)) {
            // 4xx bodies are already toasted by apiClient
            if (!(res?.status >= 400 && res?.status < 500)) message.error(res?.error || res?.message || t('Failed to add'));
            load(false); // the zone may have changed meanwhile — show the truth
            return;
        }
        const s = res.data?.summary || {};
        const parts = [];
        if (s.added) parts.push(t('{{n}} added', { n: s.added }));
        if (s.merged) parts.push(t('{{n}} merged', { n: s.merged }));
        if (s.replaced) parts.push(t('{{n}} replaced', { n: s.replaced }));
        if (s.conflict) parts.push(t('{{n}} conflicting, left unchanged', { n: s.conflict }));
        if (s.failed) {
            const errs = (res.data?.results || []).filter(r => r.status === 'failed').map(r => r.error).join('；');
            parts.push(t('{{n}} failed: {{e}}', { n: s.failed, e: errs }));
        }
        (s.failed || s.conflict ? message.warning : message.success)(parts.join('，') || t('No changes needed'), s.failed ? 6 : 3);
        seq.current++; // drop any in-flight plan request; the apply result is newer
        if (res.data?.plan) setPlan(prev => ({ ...prev, ...res.data.plan }));
        else load(false);
        setReplace([]);
        onApplied && onApplied(res.data);
    };

    const statusTag = (r) => {
        if (!hosted) {
            const st = LIVE_TAG[r.live] || null;
            return st ? <Tag color={st.color} style={{ marginInlineEnd: 0 }}>{t(st.text)}</Tag> : <Tag style={{ marginInlineEnd: 0 }}>{t('To add')}</Tag>;
        }
        const st = ZONE_TAG[r.status] || ZONE_TAG.missing;
        const tip = r.status === 'merge'
            ? t('After merge: {{v}}', { v: r.merge_value })
            : (r.existing?.length ? t('Current: {{v}}', { v: r.existing.join('；') }) : null);
        return <Tooltip title={tip}><Tag color={st.color} style={{ marginInlineEnd: 0 }}>{t(st.text)}</Tag></Tooltip>;
    };

    // inline (no hover on phones): what is there now / what the merge produces
    const detail = (r) => {
        if (!hosted) return null;
        const lines = [];
        if (r.status === 'merge') lines.push({ k: t('After merge'), v: r.merge_value, c: undefined });
        if ((r.status === 'conflict' || r.status === 'merge' || (r.status === 'present' && r.note)) && r.existing?.length) {
            lines.push({ k: t('Current'), v: r.existing.join('\n'), c: r.status === 'conflict' ? 'danger' : 'secondary' });
        }
        if (!lines.length) return null;
        return (
            <div style={{ marginTop: 4 }}>
                {lines.map(l => (
                    <div key={l.k} style={{ fontSize: 12, lineHeight: 1.5 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{l.k}：</Text>
                        <Text type={l.c} style={{ fontSize: 12, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>{l.v}</Text>
                    </div>
                ))}
            </div>
        );
    };

    const conflictControl = (r) => {
        if (!hosted || r.status !== 'conflict') return null;
        if (!r.replaceable) return <Text type="secondary" style={{ fontSize: 12 }}>{t('Fix manually')}</Text>;
        return (
            <Checkbox
                checked={replace.includes(r.key)}
                onChange={e => setReplace(v => e.target.checked ? [...v, r.key] : v.filter(k => k !== r.key))}
            >
                <Text style={{ fontSize: 12 }}>{t('Replace')}</Text>
            </Checkbox>
        );
    };

    const columns = [
        { title: t('Type'), dataIndex: 'type', width: 72, render: v => <Tag style={{ marginInlineEnd: 0 }}>{v}</Tag> },
        { title: t('Record name'), dataIndex: 'host', width: 200, render: (v, r) => <Tooltip title={r.fqdn}><span><Copyable value={v} /></span></Tooltip> },
        {
            title: t('Record value'), key: 'value',
            render: (_, r) => (
                <div>
                    <Copyable value={recordValue(r)} />
                    {r.purpose ? <div><Text type="secondary" style={{ fontSize: 12 }}>{t(r.purpose)}</Text></div> : null}
                    {detail(r)}
                </div>
            ),
        },
        {
            title: t('Status'), key: 'status', width: 120,
            render: (_, r) => <Space direction="vertical" size={2}>{statusTag(r)}{conflictControl(r)}</Space>,
        },
    ];

    const confirmText = (
        <div style={{ maxWidth: 320 }}>
            {toAdd.length > 0 && <div>{t('Add {{n}} records', { n: toAdd.length })}</div>}
            {toMerge.length > 0 && <div>{t('Merge into existing SPF: {{v}}', { v: toMerge.map(r => r.merge_value).join('；') })}</div>}
            {toReplace.length > 0 && (
                <div style={{ color: '#cf1322' }}>
                    {t('Delete and replace: {{v}}', { v: toReplace.flatMap(r => r.existing || []).join('；') })}
                </div>
            )}
        </div>
    );

    if (!app || !id) return null;
    if (loading && !plan) return <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>;
    if (error) return <Alert type="error" showIcon message={error} action={<Button size="small" onClick={() => load(false)}>{t('Retry')}</Button>} />;
    if (!plan) return null;

    const nsPending = hosted && plan.zone?.ns_status && plan.zone.ns_status !== 'active';

    return (
        <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ minWidth: 0 }}>
                    {title ? <Text strong style={{ marginInlineEnd: 8 }}>{title}</Text> : null}
                    {hosted
                        ? <Text type="secondary">{t('On Yeap DNS · {{zone}}', { zone: plan.zone?.name })}</Text>
                        : <Text type="secondary">{t('Add these at your domain\'s DNS provider')}</Text>}
                </div>
                <Space wrap size={8}>
                    <Tooltip title={t('Check public DNS')}>
                        <Button size={isMobile ? 'middle' : 'small'} icon={hosted ? <ReloadOutlined /> : <SearchOutlined />} loading={checking || loading} onClick={() => load(!hosted)}>
                            {hosted ? t('Refresh') : t('Check')}
                        </Button>
                    </Tooltip>
                    {hosted && (
                        plan.all_present && !actionable ? (
                            <Tag color="green" style={{ marginInlineEnd: 0 }}>{t('All set')}</Tag>
                        ) : (
                            <Popconfirm title={t('Change DNS?')} description={confirmText} okText={t('Confirm')} onConfirm={apply} disabled={!actionable || overQuota}>
                                <Button type="primary" size={isMobile ? 'middle' : 'small'} icon={<CloudUploadOutlined />} loading={applying} disabled={!actionable || overQuota}>
                                    {actionable ? t('Add all ({{n}})', { n: actionable }) : t('Add in one click')}
                                </Button>
                            </Popconfirm>
                        )
                    )}
                </Space>
            </div>

            {overQuota && (
                <Alert type="error" showIcon style={{ marginBottom: 8 }}
                    message={t('Not enough DNS records in your plan: {{n}} needed, {{r}} left ({{used}}/{{limit}})', { n: netNew, r: quota.remaining ?? 0, used: quota.used, limit: quota.limit })}
                    action={<Button size="small" type="primary" onClick={() => navigate(userPath(`/dns/plans?zone_id=${plan.zone?.id}`))}>{t('Upgrade plan')}</Button>} />
            )}
            {nsPending && (
                <Alert type="warning" showIcon style={{ marginBottom: 8 }}
                    message={t('The domain\'s nameservers don\'t point to Yeap DNS yet; records take effect once they do')} />
            )}
            {!hosted && (
                <Alert type="info" showIcon style={{ marginBottom: 8 }}
                    message={t('Host the domain on Yeap DNS to add these in one click')}
                    action={<Button size="small" onClick={() => navigate(userPath(`/dns/zones?add=${encodeURIComponent(plan.domain)}`))}>{t('Host it')}</Button>} />
            )}

            {isMobile ? (
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    {records.map(r => (
                        <div key={r.key} style={{ border: '1px solid rgba(128,128,128,0.25)', borderRadius: 8, padding: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                <Space size={6}><Tag style={{ marginInlineEnd: 0 }}>{r.type}</Tag><Text type="secondary" style={{ fontSize: 12 }}>{r.purpose ? t(r.purpose) : ''}</Text></Space>
                                {statusTag(r)}
                            </div>
                            <div style={{ marginTop: 6 }}><Text type="secondary" style={{ fontSize: 12 }}>{t('Record name')}</Text><div><Copyable value={r.host} /></div></div>
                            <div style={{ marginTop: 4 }}><Text type="secondary" style={{ fontSize: 12 }}>{t('Record value')}</Text><div><Copyable value={recordValue(r)} /></div></div>
                            {detail(r)}
                            {conflictControl(r) && <div style={{ marginTop: 6 }}>{conflictControl(r)}</div>}
                        </div>
                    ))}
                </Space>
            ) : (
                <Table size="small" pagination={false} rowKey="key" dataSource={records} columns={columns} scroll={{ x: 640 }} />
            )}
        </div>
    );
};

export default DnsOneClick;
